import { useState, useEffect } from "react";
import { Plus, Edit, MoreHorizontal, Check, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAppAuth } from "@/lib/auth";
import { subscriptionApi } from "@/lib/api/subscriptions";
import type { Plan } from "@/types/subscriptions";
import { Skeleton } from "@/components/ui/skeleton";

export function PlansPage() {
  const { token, getToken } = useAppAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    descriptionSv: "",
    price: 0,
    eurPrice: 0,
    videosPerMonth: 0,
    active: true,
  });

  const fetchPlans = async () => {
    // Get fresh token
    const freshToken = await getToken();
    if (!freshToken) return;
    
    try {
      setLoading(true);
      const response = await subscriptionApi.listPlans(freshToken);
      setPlans(response.plans);
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [token]);

  const handleSync = async () => {
    const freshToken = await getToken();
    if (!freshToken) {
        toast.error("Authentication session expired. Please refresh the page.");
        return;
    }
    
    try {
      setSyncing(true);
      const result = await subscriptionApi.syncPlans(freshToken);
      toast.success(`Synced ${result.synced} plans from Stripe`);
      if (result.errors.length > 0) {
        toast.warning(`Some errors occurred: ${result.errors.join(", ")}`);
      }
      fetchPlans();
    } catch (error) {
      console.error("Failed to sync plans:", error);
      toast.error("Failed to sync plans from Stripe");
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenDialog = (plan?: Plan) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description || "",
        descriptionSv: plan.descriptionTranslations?.sv || "",
        price: plan.price / 100, // Convert cents to units
        eurPrice: plan.eurPrice ? plan.eurPrice / 100 : 0,
        videosPerMonth: plan.videosPerMonth,
        active: plan.active,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: "",
        description: "",
        descriptionSv: "",
        price: 0,
        eurPrice: 0,
        videosPerMonth: 0,
        active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const freshToken = await getToken();
    if (!freshToken) {
        toast.error("Session expired");
        return;
    }

    try {
      const priceInCents = Math.round(formData.price * 100);
      const eurPriceInCents = formData.eurPrice > 0 ? Math.round(formData.eurPrice * 100) : undefined;
      const descriptionTranslations = formData.descriptionSv 
        ? { sv: formData.descriptionSv } 
        : undefined;

      if (editingPlan) {
        await subscriptionApi.updatePlan(freshToken, editingPlan.id, {
          name: formData.name,
          description: formData.description,
          descriptionTranslations,
          price: priceInCents,
          eurPrice: eurPriceInCents,
          videosPerMonth: formData.videosPerMonth,
          active: formData.active,
        });
        toast.success("Plan updated successfully");
      } else {
        await subscriptionApi.createPlan(freshToken, {
          name: formData.name,
          description: formData.description,
          descriptionTranslations,
          price: priceInCents,
          eurPrice: eurPriceInCents,
          videosPerMonth: formData.videosPerMonth,
        });
        toast.success("Plan created successfully");
      }

      setIsDialogOpen(false);
      fetchPlans();
    } catch (error) {
      console.error("Failed to save plan:", error);
      toast.error("Failed to save plan");
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          {loading ? (
            <>
              <Skeleton className="h-9 w-32 mb-2" />
              <Skeleton className="h-5 w-64" />
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold tracking-tight">Plans</h1>
              <p className="text-muted-foreground">
                Manage subscription plans and pricing
              </p>
            </>
          )}
        </div>
        <div className="flex gap-2">
          {loading ? (
            <>
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-10 w-32" />
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleSync} disabled={syncing}>
                <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                Sync from Stripe
              </Button>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                Add Plan
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Plans</CardTitle>
          <CardDescription>
            List of all subscription plans available in the system.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Price (SEK)</TableHead>
                <TableHead>Price (EUR)</TableHead>
                <TableHead>Quota</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Stripe ID</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[150px]" />
                        <Skeleton className="h-3 w-[200px]" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[80px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[60px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[120px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-8 rounded-md" />
                    </TableCell>
                  </TableRow>
                ))
              ) : plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    No plans found. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">
                      <div>{plan.name}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                        {plan.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatCurrency(plan.price, plan.currency)} / month
                    </TableCell>
                    <TableCell>
                      {plan.eurPrice
                        ? `${formatCurrency(plan.eurPrice, "eur")} / month`
                        : <span className="text-muted-foreground text-xs">Not set</span>
                      }
                    </TableCell>
                    <TableCell>{plan.videosPerMonth} videos/mo</TableCell>
                    <TableCell>
                      {plan.active ? (
                        <div className="flex items-center text-green-600">
                          <Check className="mr-1 h-3 w-3" />
                          Active
                        </div>
                      ) : (
                        <div className="flex items-center text-muted-foreground">
                          <X className="mr-1 h-3 w-3" />
                          Inactive
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {plan.stripeProductId}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleOpenDialog(plan)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? "Edit Plan" : "Create Plan"}
            </DialogTitle>
            <DialogDescription>
              {editingPlan
                ? "Update plan details. Changing price will create a new Stripe Price."
                : "Create a new subscription plan. This will create a Product and Price in Stripe."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="col-span-3"
                  required
                />
              </div>

              <div className="col-span-4">
                <Label className="mb-2 block">Description</Label>
                <Tabs defaultValue="en" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="en">English</TabsTrigger>
                    <TabsTrigger value="sv">Swedish</TabsTrigger>
                  </TabsList>
                  <TabsContent value="en">
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder="Plan description in English"
                      className="min-h-[100px]"
                    />
                  </TabsContent>
                  <TabsContent value="sv">
                    <Textarea
                      id="descriptionSv"
                      value={formData.descriptionSv}
                      onChange={(e) =>
                        setFormData({ ...formData, descriptionSv: e.target.value })
                      }
                      placeholder="Planbeskrivning på svenska (Plan description in Swedish)"
                      className="min-h-[100px]"
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <div className={`grid gap-4 ${editingPlan ? "grid-cols-3" : "grid-cols-2"}`}>
                <div className="grid gap-2">
                  <Label htmlFor="price">Price (SEK)</Label>
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price: parseFloat(e.target.value),
                      })
                    }
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="eurPrice">Price (EUR €)</Label>
                  <Input
                    id="eurPrice"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 29.00"
                    value={formData.eurPrice || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        eurPrice: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="videos">Videos/mo</Label>
                  <Input
                    id="videos"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.videosPerMonth}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        videosPerMonth: parseInt(e.target.value),
                      })
                    }
                    required
                  />
                </div>

                {editingPlan && (
                  <div className="grid gap-2">
                    <Label htmlFor="active">Active</Label>
                    <div className="flex h-10 items-center">
                      <Switch
                        id="active"
                        checked={formData.active}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, active: checked })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="submit">Save changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
